import { postToBackend } from "../portalClient";
import { activePortal } from "helpers/portalSession";
import registry from "./registry.json";
import fixtures from "./fixtures.json";

/**
 * One caller for every endpoint in the Admin & Partner Portal API Specification.
 *
 * ── The two hops ────────────────────────────────────────────────────────────
 *   portal ──POST /app/v1/…──> app-backend ──spec REST /api/v1/…──> partner-engine
 *
 * The portal→app-backend hop is POST-only, so each endpoint has a POST path with
 * the operation carried in the path (`/customers/list`, `/deal-codes/delete`).
 * The spec's own method and path — `GET /api/v1/customers`, `DELETE
 * /api/v1/deal-codes/{id}` — describe the OUTBOUND leg that app-backend makes to
 * partner-engine, and are recorded in registry.json next to each entry so the two
 * never drift.
 *
 * ── Live vs stubbed ─────────────────────────────────────────────────────────
 * 60 of the 132 endpoints exist on app-backend today: tenants, roles, and the
 * Part B partner areas (billing, reports, customers, manual booking, servicing,
 * communications, markup rules, service fees, users, branding). The rest resolve
 * to a fixture derived from the spec's own Response Example, so screens render
 * real-shaped data before the backend lands.
 *
 * ⚠️ `path` is the app-backend route, NOT the spec's REST path. app-backend
 * mounts these under `/app/v1/partners/...` (plural) and names several
 * operations differently from the spec — invite, remove, claim, toggle-status,
 * view, upload. `engine` records the spec's own method+path for traceability;
 * it is documentation, never something to call. Verify any new entry against
 * app-backend/src/routes/partner*.ts rather than deriving it from the spec.
 *
 * Promoting one is a ONE-LINE change: set `"live": true` on its registry entry.
 * Nothing at the call site changes, because a stub and a live call return the
 * same `{ ok, data }` envelope. `REACT_APP_SPEC_MOCKS=false` forces every
 * endpoint to call for real, which is how you smoke-test a freshly deployed batch.
 *
 * ⚠️ A stubbed response is NOT persistence. Writes resolve successfully and
 * change nothing server-side, so a create followed by a list will not show the
 * new row. Screens should treat `stub: true` as "not saved yet" if they surface
 * anything to the user about it.
 */

/** Mocks are on unless explicitly disabled, so a normal `npm start` works offline. */
const MOCKS_ENABLED = process.env.REACT_APP_SPEC_MOCKS !== "false";

/** Fixtures are shared module state; hand out copies so a screen cannot mutate them. */
const clone = (v) => (v === undefined ? undefined : JSON.parse(JSON.stringify(v)));

/** A beat of latency, so loading states are exercised rather than skipped. */
const settle = () => new Promise((r) => setTimeout(r, 120));

/**
 * Call a spec endpoint by its registry name.
 *
 * Returns the same shape in both modes:
 *   { ok: true,  data }               — plus `stub: true` when served from a fixture
 *   { ok: false, message }            — server wording, or why the stub cannot answer
 *
 * `portal` defaults to the portal the page is in rather than to `"crm"`, so a
 * module shared by both — reference.js — signs its call with the session the
 * user actually holds. Portal-specific modules keep passing theirs explicitly.
 */
export const callSpec = async (name, body = {}, portal = activePortal()) => {
  const entry = registry[name];
  if (!entry) {
    // A typo'd name would otherwise fail silently as an empty screen.
    return { ok: false, message: `Unknown API endpoint "${name}".` };
  }

  if (entry.live || !MOCKS_ENABLED) {
    return postToBackend(entry.path, body, portal);
  }

  const fixture = fixtures[name];
  if (fixture === undefined) {
    return { ok: false, message: `${entry.title} is not available yet.` };
  }

  await settle();
  return { ok: true, data: clone(fixture), stub: true };
};

/** Registry metadata for an endpoint — path, spec origin, live flag, spec sections. */
export const specEntry = (name) => registry[name];

/** Every endpoint still stubbed. Useful for a readiness view or a smoke test. */
export const stubbedEndpoints = () =>
  Object.entries(registry)
    .filter(([, v]) => !v.live)
    .map(([name, v]) => ({ name, path: v.path, engine: v.engine, title: v.title, secs: v.secs }));
